<template>
  <div ref="wrapEl" class="lb-wrap">
    <!-- Empty state -->
    <div v-if="!groups.length" class="lb-empty">
      <v-icon size="40" color="grey">mdi-calendar-blank-outline</v-icon>
      <p class="mt-2">{{ emptyText || t('schedule.board.empty') }}</p>
    </div>

    <!-- Day groups (each in a bordered card, collapsible) -->
    <div
      v-for="g in groups"
      :key="g.iso"
      class="lb-group"
      :class="{ 'lb-collapsed': !isOpen(g.iso) }"
      :data-iso="g.iso"
    >
      <div class="lb-date" :class="{ clickable: g.iso !== todayIso }" @click="toggle(g.iso)">
        <v-icon v-if="g.iso !== todayIso" size="16" class="lb-chevron">
          {{ isOpen(g.iso) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
        </v-icon>
        <span>{{ g.dateLabel }}</span>
        <span v-if="g.relLabel" class="lb-rel">— {{ g.relLabel }}</span>
        <span class="lb-count">{{ g.lessons.length }}</span>
      </div>

      <template v-if="isOpen(g.iso)">
        <!-- Day-off banners -->
        <div
          v-for="off in g.daysOff"
          :key="`off-${off.id}-${off.instructorId}`"
          class="lb-dayoff"
        >
          <v-icon size="16" class="lb-dayoff-ic">mdi-beach</v-icon>
          <span class="lb-dayoff-txt">
            {{ t('dayOff.badge') }}
            <template v-if="off.startTime && off.endTime"> {{ off.startTime }}–{{ off.endTime }}</template>
            <template v-if="showInstructor && off.instructorName"> — {{ off.instructorName }}</template>
            <template v-if="off.reason"> · {{ off.reason }}</template>
          </span>
          <button class="lb-dayoff-x" type="button" :title="t('dayOff.remove')" @click.stop="emit('remove-dayoff', off)">
            <v-icon size="16">mdi-close</v-icon>
          </button>
        </div>

        <div
          v-for="lesson in g.lessons"
          :key="lesson.id"
          class="lb-row"
          :data-min="slotStart(lesson.timeSlot)"
          :class="{ done: lesson.status === 'COMPLETED', cancelled: lesson.status === 'CANCELLED', 'lb-now': isNow(lesson) }"
          @click="emit('open', lesson)"
        >
        <button class="lb-chk" type="button" @click.stop="emit('toggle', lesson)">
          <svg v-if="lesson.status === 'COMPLETED'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            <path d="M5 12l5 5L20 7"/>
          </svg>
        </button>

        <div class="lb-content">
          <span class="lb-time">{{ startTime(lesson.timeSlot) }}</span>
          <span v-if="lesson.studentName" class="lb-name">{{ lesson.studentName }}</span>
          <a
            v-if="lesson.phone"
            class="lb-phone"
            :href="`tel:${cleanPhone(lesson.phone)}`"
            @click.stop.prevent="call(lesson.phone)"
          >{{ lesson.phone }}</a>
          <span v-if="lesson.addressText" class="lb-addr">{{ shortAddr(lesson.addressText) }}</span>
          <span v-if="showInstructor && lesson.instructorName" class="lb-instr">{{ lesson.instructorName }}</span>
          <span v-if="lesson.note" class="lb-note">{{ lesson.note }}</span>
        </div>
        <button
          class="lb-waybill"
          type="button"
          title="Жол парағын жүктеу / Скачать путевой лист"
          aria-label="Жол парағын жүктеу / Скачать путевой лист"
          @click.stop="emit('download-waybill', lesson)"
        >
          <v-icon size="20">mdi-file-pdf-box</v-icon>
        </button>
      </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  lessons:        { type: Array,   default: () => [] },
  daysOff:        { type: Array,   default: () => [] },
  showInstructor: { type: Boolean, default: false },
  emptyText:      { type: String,  default: '' }
})
const emit = defineEmits(['open', 'toggle', 'remove-dayoff', 'download-waybill'])

const { t } = useI18n()

const wrapEl = ref(null)

// Collapsible days: today always open; past/future collapsed until clicked
const expanded = ref(new Set())
const isOpen = (iso) => iso === todayIso.value || expanded.value.has(iso)
const toggle = (iso) => {
  if (iso === todayIso.value) return        // today stays always open
  const s = new Set(expanded.value)
  s.has(iso) ? s.delete(iso) : s.add(iso)
  expanded.value = s
}

const toLocalIso = (d) => {
  const yyyy = d.getFullYear()
  const mm   = String(d.getMonth() + 1).padStart(2, '0')
  const dd   = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const todayIso    = computed(() => toLocalIso(new Date()))
const tomorrowIso = computed(() => toLocalIso(new Date(Date.now() + 86400000)))

const cleanPhone = (p) => String(p || '').replace(/[^\d+]/g, '')

// Show only start time: "10:00-11:00" -> "10:00"
const startTime = (slot) => String(slot || '').split('-')[0]

// Strip trailing "(KORKEM AVTO)" type branch suffix, keep street
const shortAddr = (text) => {
  if (!text) return ''
  const stripped = text.replace(/\s*\([^)]*\)\s*$/, '').trim()
  const result = stripped || text
  return result.length > 32 ? result.slice(0, 30) + '…' : result
}

const slotStart = (slot) => {
  const m = String(slot || '').match(/^(\d{1,2}):(\d{2})/)
  return m ? Number(m[1]) * 60 + Number(m[2]) : 9999
}

// ── Current ongoing lesson (animated highlight) ──
const nowMin = ref(0)
const updateNow = () => { const d = new Date(); nowMin.value = d.getHours() * 60 + d.getMinutes() }
let nowTimer = null
onMounted(() => {
  updateNow()
  nowTimer = setInterval(updateNow, 60000)
  window.addEventListener('focus', updateNow)
  document.addEventListener('visibilitychange', updateNow)
})
onUnmounted(() => {
  clearInterval(nowTimer)
  window.removeEventListener('focus', updateNow)
  document.removeEventListener('visibilitychange', updateNow)
})

// Today's lessons that are ongoing OR still upcoming (not finished/done/cancelled)
const isNow = (lesson) => {
  if (!lesson || lesson.dateIso !== todayIso.value) return false
  if (lesson.status !== 'SCHEDULED') return false
  const m = String(lesson.timeSlot || '').match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/)
  if (m) {
    const end = +m[3] * 60 + +m[4]
    return nowMin.value < end          // ongoing or upcoming
  }
  // No end time — fall back to start time
  return nowMin.value < slotStart(lesson.timeSlot)
}

const call = (phone) => {
  const num = cleanPhone(phone)
  if (num) window.location.href = `tel:${num}`
}

const groups = computed(() => {
  const map = new Map()
  for (const l of props.lessons) {
    if (!l?.dateIso) continue
    if (!map.has(l.dateIso)) map.set(l.dateIso, { lessons: [], daysOff: [] })
    map.get(l.dateIso).lessons.push(l)
  }
  // Merge day-off markers — they may fall on days with no lessons too
  for (const off of props.daysOff) {
    if (!off?.dateIso) continue
    if (!map.has(off.dateIso)) map.set(off.dateIso, { lessons: [], daysOff: [] })
    map.get(off.dateIso).daysOff.push(off)
  }

  const today    = todayIso.value
  const tomorrow = tomorrowIso.value

  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([iso, { lessons, daysOff }]) => {
      const d   = new Date(iso + 'T00:00:00')
      const day = String(d.getDate()).padStart(2, '0')
      const mon = String(d.getMonth() + 1).padStart(2, '0')
      let relLabel = ''
      if (iso === today)    relLabel = t('schedule.board.today')
      if (iso === tomorrow) relLabel = t('schedule.board.tomorrow')
      return {
        iso,
        dateLabel: `${day}.${mon}`,
        relLabel,
        lessons: [...lessons].sort((a, b) => slotStart(a.timeSlot) - slotStart(b.timeSlot)),
        daysOff
      }
    })
})

// Scroll to today's group (and current-hour row within it)
const scrollToToday = async () => {
  await nextTick()
  const wrap = wrapEl.value
  if (!wrap) return
  const today = todayIso.value
  let target = wrap.querySelector(`[data-iso="${today}"]`)
  if (target) {
    // Within today, jump to the first lesson at/after the current time
    const nowMin = new Date().getHours() * 60 + new Date().getMinutes()
    const rows = [...target.querySelectorAll('.lb-row')]
    const row = rows.find(r => Number(r.dataset.min) >= nowMin)
    if (row) target = row
  } else {
    // No lessons today — go to the nearest upcoming day
    const els = [...wrap.querySelectorAll('.lb-group')]
    target = els.find(el => el.dataset.iso >= today) || els[0]
  }
  if (!target) return
  const y = target.getBoundingClientRect().top + window.scrollY - 90
  window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
}

defineExpose({ scrollToToday })
</script>

<style scoped>
.lb-wrap { padding: 4px 0 80px; }

.lb-empty {
  text-align: center;
  padding: 48px 16px;
  color: rgba(var(--v-theme-on-surface), 0.45);
  font-size: .9rem;
}

/* Each day = a bordered card */
.lb-group {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
  border-radius: 12px;
  padding: 10px 14px 8px;
  margin-bottom: 12px;
}

.lb-group.lb-collapsed { padding-bottom: 10px; }

.lb-date {
  font-size: .8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.lb-date.clickable { cursor: pointer; user-select: none; }
.lb-date.clickable:hover { color: rgba(var(--v-theme-on-surface), 0.9); }
.lb-group.lb-collapsed .lb-date { margin-bottom: 0; }
.lb-chevron { color: rgba(var(--v-theme-on-surface), 0.5); margin-left: -2px; }
.lb-rel { color: rgba(var(--v-theme-on-surface), 0.45); font-weight: 500; }
.lb-count {
  margin-left: auto;
  font-size: .72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.55);
  background: rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 10px;
  padding: 1px 8px;
}

/* Day-off banner */
.lb-dayoff {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin: 4px 0 6px;
  border-radius: 8px;
  background: rgba(176, 141, 87, 0.12);
  border: 1px dashed rgba(176, 141, 87, 0.45);
}
.lb-dayoff-ic { color: #c2a36a; flex-shrink: 0; }
.lb-dayoff-txt {
  flex: 1;
  font-size: .82rem;
  font-weight: 600;
  color: #c2a36a;
  word-break: break-word;
}
.lb-dayoff-x {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 24px; height: 24px;
  border-radius: 6px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.5);
  cursor: pointer;
  padding: 0;
}
.lb-dayoff-x:hover { background: rgba(var(--v-theme-on-surface), 0.1); color: rgb(var(--v-theme-error)); }

/* Lesson row — bigger tap target */
.lb-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 6px;
  cursor: pointer;
  transition: background .1s;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}
.lb-row:last-child { border-bottom: none; }
.lb-row:hover { background: rgba(var(--v-theme-on-surface), 0.05); }
.lb-row:active { background: rgba(var(--v-theme-on-surface), 0.08); }

/* Currently ongoing lesson — soft, muted animated glow (not bright) */
.lb-row.lb-now {
  border-radius: 8px;
  border-bottom-color: transparent;
  animation: lbNowPulse 2.6s ease-in-out infinite;
}
@keyframes lbNowPulse {
  0%, 100% { background-color: rgba(194, 163, 106, 0.06); }
  50%      { background-color: rgba(194, 163, 106, 0.17); }
}
@media (prefers-reduced-motion: reduce) {
  .lb-row.lb-now { animation: none; background-color: rgba(194, 163, 106, 0.12); }
}
.lb-row.done { opacity: .5; }
.lb-row.done .lb-content { text-decoration: line-through; }
.lb-row.cancelled { opacity: .4; }
.lb-row.cancelled .lb-content { text-decoration: line-through; }

.lb-chk {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.35);
  background: transparent;
  flex-shrink: 0;
  margin-top: 1px;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  color: rgb(var(--v-theme-success));
}
.lb-chk svg { width: 13px; height: 13px; }
.lb-row.done .lb-chk { background: rgb(var(--v-theme-success)); border-color: rgb(var(--v-theme-success)); color: #fff; }

.lb-waybill {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  margin-top: -5px;
  border: 1px solid rgba(211, 47, 47, .28);
  border-radius: 8px;
  background: rgba(211, 47, 47, .08);
  color: #d94a4a;
  cursor: pointer;
  transition: background .15s, border-color .15s, transform .15s;
}
.lb-waybill:hover {
  background: rgba(211, 47, 47, .16);
  border-color: rgba(211, 47, 47, .5);
  transform: translateY(-1px);
}
.lb-waybill:active { transform: translateY(0); }

.lb-content {
  font-size: .9rem;
  font-weight: 400;
  line-height: 1.55;
  flex: 1;
  word-break: break-word;
  padding-top: 1px;
}
.lb-content > * { margin-right: 6px; font-weight: 400; }

/* Each part — its own SOFT, muted (desaturated) color, easy on the eyes */
.lb-time  { color: #c2a36a; }   /* жұмсақ алтын — уақыт */
.lb-name  { color: #cfcfd4; }   /* солғын ақ — аты */
.lb-phone { color: #7fa0c4; text-decoration: none; }  /* жұмсақ көк — нөмір */
.lb-phone:hover { text-decoration: underline; }
.lb-addr  { color: #84ab8d; }   /* жұмсақ жасыл — мекенжай */
.lb-instr { color: #b394c9; font-size: .76rem; }  /* жұмсақ күлгін — инструктор */
.lb-note  { color: #b59a82; font-style: italic; } /* жұмсақ қоңыр — ескертпе */
</style>
